function findCurrentRouteConfiguration (sitemapItem, currentRoute) {
    for (const item of sitemapItem) {
        if (item.route === currentRoute) {
            return item
        }

        if (item.children) {
            const child = findCurrentRouteConfiguration(item.children, currentRoute)

            if (child) {
                return child
            }
        }
    }

    return null
}

export function getBreadcrumbs (sitemap, currentRoute, currentRouteParameters, props) {
    // Find the route configuration of the currently active route
    const currentRouteConfiguration = findCurrentRouteConfiguration(sitemap, currentRoute)

    // If the current route configuration is found, and it defines a custom breadcrumbs resolver
    // then, use that function to return the information
    if (currentRouteConfiguration?.breadcrumbsResolver) {
        return currentRouteConfiguration.breadcrumbsResolver(props)
    }

    return buildBreadcrumbs(sitemap, currentRoute, currentRouteParameters)
}

function buildBreadcrumbs (sitemap, currentRoute, currentRouteParameters) {
    // Placeholder variable for when we are searching children of some sitemap entry
    let childSearchResult = null

    // Starting to loop over sitemap items
    for (const sitemapItem of sitemap) {
        if (sitemapItem.route === currentRoute) { // Check if the current item is the one we are searching for
            return formatBreadcrumbEntry(sitemapItem, currentRouteParameters, childSearchResult, true)
        } else if (sitemapItem.children && sitemapItem.children.length > 0) { // Check if current item has children defined
            // Recursively call buildBreadcrumbs function with current item children as a sitemap
            // This will go over all children of all items in the sitemap until it finds a match
            childSearchResult = buildBreadcrumbs(sitemapItem.children, currentRoute, currentRouteParameters)

            if (childSearchResult !== null) {
                let parentTrace = formatBreadcrumbEntry(sitemapItem, currentRouteParameters, childSearchResult)

                // We convert it to array for easier formatting of the result
                if (Array.isArray(parentTrace) === false) {
                    parentTrace = [parentTrace]
                }

                // The found childSearchResult is not an array only when the exact route that we were searching for was found
                // We convert it to array for easier formatting of the result
                if (Array.isArray(childSearchResult) === false) {
                    childSearchResult = [childSearchResult]
                }

                // Merge parent trace and child result
                return [...parentTrace, ...childSearchResult]
            }
        }
    }

    // Failed to build breadcrumbs, return null
    return null
}

function formatBreadcrumbEntry (sitemapItem, currentRouteParameters, childSearchResult, isLastBreadcrumb) {
    if (sitemapItem.isStandaloneItem) {
        return []
    }

    // If the sitemapItem contains breadcrumb parameters we will add that breadcrumb item additionally
    if (sitemapItem.identifier) {
        return [
            {
                href: {
                    route: sitemapItem.identifier.route,
                    params: sitemapItem.identifier.parameters
                },
                text: currentRouteParameters[sitemapItem.identifier.parameterLabel]
            },
            {
                href: {
                    route: sitemapItem.route,
                    params: sitemapItem.identifier.parameters
                },
                text: sitemapItem.label
            }
        ]
    }

    let route = isLastBreadcrumb
        ? {
            route: '',
            params: {}
        }
        : {
            route: sitemapItem.route,
            params: {}
        }

    // Set route sidebar dropdown item with for route (first route in breadcrumbs if sidebar is dropdown)
    if (sitemapItem.route.length === 0 && childSearchResult) {
        route = childSearchResult.length > 1 ? childSearchResult[0].href : childSearchResult.href
    }

    return {
        href: route,
        text: sitemapItem.label
    }
}

export function getActiveSidebarItem (sitemapItem, currentRoute, props) {
    const activeRoutes = []
    const expandDropdowns = []

    // Find the route configuration of the currently active route
    const currentRouteConfiguration = findCurrentRouteConfiguration(sitemapItem, currentRoute)

    // If the current route configuration is found, and it defines a custom active sidebar item resolver
    // then, use that function to return the information
    if (currentRouteConfiguration?.activeSidebarItemResolver) {
        return currentRouteConfiguration.activeSidebarItemResolver(props)
    }

    function execute (sitemapItem, currentRoute) {
        for (const item of sitemapItem) {
            if (item.route === currentRoute) { // Check if the current item is the one we are searching for
                // Push item route to activeRoutes to mark active route
                activeRoutes.push(item.activeSidebarRoute ? item.activeSidebarRoute : item.route)
                // Check if item have sidebarItemName and push sidebar item name to expandDropdowns to expand the drop-down menu
                if (item.sidebarItemName) {
                    expandDropdowns.push(item.sidebarItemName)
                }

                return item
            }

            if (item.children) {
                const child = execute(item.children, currentRoute)
                if (child) {
                    // Push child route to activeRoutes to mark active route
                    activeRoutes.push(item.activeSidebarRoute ? item.activeSidebarRoute : item.route)
                    // Check if item have sidebarItemName and push sidebar item name to expandDropdowns to expand the drop-down menu
                    if (item.sidebarItemName) {
                        expandDropdowns.push(item.sidebarItemName)
                    }
                    // Check if child have sidebarItemName and push sidebar item name to expandDropdowns to expand the drop-down menu
                    if (child.sidebarItemName) {
                        expandDropdowns.push(child.sidebarItemName)
                    }

                    return child
                }
            }
        }
    }

    execute(sitemapItem, currentRoute)

    return {
        activeRoutes: activeRoutes,
        expandDropdowns: expandDropdowns
    }
}
